/**
 * Guided tour definitions (Driver.js). Pure data — no DOM, no Vue.
 *
 * Every targeted `element` is a `[data-tour="…"]` selector. The attributes live
 * on AppLayout chrome (sidebar items via NavItem.tour, header controls inline),
 * so a tour works on any page under its `routePrefix`.
 *
 * Steps without `element` render as hero cards (welcome / finish).
 * `data.preview` draws the card header: a mock of the real sidebar, rows of
 * icon + label, `active` = highlighted the way the live nav highlights it.
 *
 * Bump a tour's `id` (e.g. advisor-v2) when its steps change materially —
 * the completion flag is keyed by id, so existing users see the new tour once.
 */
import type { DriveStep } from 'driver.js'

export type PreviewRow = { icon: string; label: string; active?: boolean }

export type TourStep = DriveStep & {
    /** Navigate here before showing the step (multi-page tours). */
    route?: string
    data?: {
        hero?: boolean
        preview?: PreviewRow[]
        /** Which chrome the header mock imitates. Default: sidebar. */
        mock?: 'sidebar' | 'search' | 'plus' | 'avatar'
        /** Omit the "+" quick-add button from the header mock (supplier header has none). */
        hideQuickAdd?: boolean
    }
}

export type Tour = {
    id: string
    /** Auto-start only when the current route starts with this. */
    routePrefix: string
    steps: TourStep[]
}

const el = (key: string) => `[data-tour="${key}"]`
const row = (icon: string, label: string, active = false): PreviewRow => ({ icon, label, active })

const hero = (title: string, description: string, preview: PreviewRow[], btn: 'next' | 'done', text: string): TourStep => ({
    data: { hero: true, preview },
    popover: {
        // Welcome sits bottom-right; the finish card is centered on screen.
        title, description, popoverClass: 'cc-tour cc-tour-hero' + (btn === 'done' ? ' cc-tour-center' : ''),
        ...(btn === 'next' ? { nextBtnText: text } : { doneBtnText: text }),
    },
})

// Real sidebar rows, reused so the mock matches what the user sees.
const adv = {
    today: (a = false) => row('LayoutDashboard', 'Today', a),
    pipeline: (a = false) => row('Kanban', 'Pipeline', a),
    accounts: (a = false) => row('Building', 'Accounts', a),
    suppliers: (a = false) => row('Building2', 'Supplier Directory', a),
    mdf: (a = false) => row('DollarSign', 'MDF Requests', a),
}
const sup = {
    profile: (a = false) => row('Building2', 'Company Profile', a),
    products: (a = false) => row('Package', 'Products', a),
    promotions: (a = false) => row('Target', 'Promotions', a),
    quotes: (a = false) => row('MessageSquare', 'Quotes', a),
    mdfRequests: (a = false) => row('DollarSign', 'MDF Requests', a),
    mdfOfferings: (a = false) => row('Megaphone', 'MDF Offerings', a),
}

export const advisorTour: Tour = {
    id: 'advisor-v1',
    routePrefix: '/advisor',
    steps: [
        hero('Welcome to Channel Companion',
            'A quick walkthrough of where things live. Takes about a minute, and you can skip any time.',
            [adv.today(true), adv.accounts(), adv.suppliers()], 'next', 'Show me around'),
        {
            element: el('nav-today'),
            data: { preview: [adv.today(true), adv.pipeline(), adv.accounts()] },
            popover: {
                title: 'Start your day here',
                description: 'Today shows your pipeline at a glance, upcoming meetings, and active supplier promotions.',
                side: 'right', align: 'start',
            },
        },
        {
            element: el('nav-accounts'),
            data: { preview: [adv.today(), adv.accounts(true), adv.suppliers()] },
            popover: {
                title: 'Your accounts',
                description: 'Every client lives here. Open one for meeting prep, AI briefings, and deal tracking.',
                side: 'right', align: 'start',
            },
        },
        {
            element: el('nav-suppliers'),
            data: { preview: [adv.accounts(), adv.suppliers(true), adv.mdf()] },
            popover: {
                title: 'Find suppliers',
                description: 'Browse and compare suppliers, see ratings, and request quotes for your clients.',
                side: 'right', align: 'start',
            },
        },
        {
            element: el('search'),
            data: { mock: 'search', preview: [row('Building', 'Acme Manufacturing', true), row('Building2', 'Acme Telecom'), row('Package', 'Acme Voice')] },
            popover: {
                title: 'Search anything',
                description: 'Jump to accounts, suppliers, or products from any page. Press Cmd+K to focus it.',
                side: 'bottom', align: 'start',
            },
        },
        {
            element: el('quick-add'),
            data: { mock: 'plus', preview: [row('Building', 'New account', true), row('Activity', 'Log activity'), row('Zap', 'Quick match')] },
            popover: {
                title: 'Add an account fast',
                description: 'Create a new account or log an activity without leaving the page you are on.',
                side: 'bottom', align: 'end',
            },
        },
        {
            element: el('account-menu'),
            data: { mock: 'avatar', preview: [row('FileText', 'Documentation'), row('LifeBuoy', 'Help & Support'), row('Compass', 'Take the tour', true)] },
            popover: {
                title: 'Help is one click away',
                description: 'Documentation, support, and a "Take the tour" link to replay this walkthrough.',
                side: 'bottom', align: 'end',
            },
        },
        hero('You’re all set',
            'Head to Today to see what needs attention, or add your first account from the + button.',
            [adv.today(true), adv.accounts(), adv.suppliers()],
            'done', 'Get started'),
    ],
}

export const supplierTour: Tour = {
    id: 'supplier-v1',
    routePrefix: '/supplier',
    steps: [
        hero('Welcome to Channel Companion',
            'A quick walkthrough of your supplier workspace. Takes about a minute, and you can skip any time.',
            [sup.profile(true), sup.products(), sup.promotions()], 'next', 'Show me around'),
        {
            element: el('nav-profile'),
            data: { preview: [sup.profile(true), sup.products(), sup.promotions()] },
            popover: {
                title: 'Your company profile',
                description: 'This is what advisors see. Keep logo, description, and contacts current.',
                side: 'right', align: 'start',
            },
        },
        {
            element: el('nav-products'),
            data: { preview: [sup.profile(), sup.products(true), sup.promotions()] },
            popover: {
                title: 'Products',
                description: 'Your catalog, mapped to solution areas so advisors can find the right fit.',
                side: 'right', align: 'start',
            },
        },
        {
            element: el('nav-promotions'),
            data: { preview: [sup.products(), sup.promotions(true), sup.quotes()] },
            popover: {
                title: 'Promotions',
                description: 'Publish offers that appear on every advisor’s Today page.',
                side: 'right', align: 'start',
            },
        },
        {
            element: el('nav-mdf-requests'),
            data: { preview: [sup.promotions(), sup.mdfRequests(true), sup.mdfOfferings()] },
            popover: {
                title: 'MDF requests',
                description: 'Review and approve market development fund requests from advisors.',
                side: 'right', align: 'start',
            },
        },
        {
            element: el('account-menu'),
            data: { mock: 'avatar', hideQuickAdd: true, preview: [row('FileText', 'Documentation'), row('LifeBuoy', 'Help & Support'), row('Compass', 'Take the tour', true)] },
            popover: {
                title: 'Help is one click away',
                description: 'Documentation, support, and a "Take the tour" link to replay this walkthrough.',
                side: 'bottom', align: 'end',
            },
        },
        hero('You’re all set',
            'Start with your company profile. It is the first thing advisors see.',
            [sup.profile(true), sup.products(), sup.promotions()], 'done', 'Get started'),
    ],
}

export function tourForRole(role: string | null | undefined): Tour | null {
    switch (role) {
        case 'ADVISOR': return advisorTour
        case 'SUPPLIER_ADMIN':
        case 'SUPPLIER_USER': return supplierTour
        default: return null // ADMIN / CHANNEL_MANAGER: no tour yet
    }
}
